import React, { useState, useEffect, useRef, Key, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { OpTable } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpPage } from 'components/customAntd/OpPage/OpPage';
import { OpCard } from 'components/customAntd/DLS/OpCard/OpCard';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import DateRangeLocationFilter2 from 'components/customAntd/DateRangeLocationFilter2';
import { getStatusNameById, getStatusColor, getVisitorDateTime } from 'utils/visitorsHelper';
import { Bar } from 'react-chartjs-2';
import { AppDispatch, RootState } from 'store/store';
import { fetchVisits, setSelectedVisit, setSelectedVisitor, setVisitsEndDate, setVisitsStartDate } from 'store/slices/visitsSlice';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { Visitor, VisitStatus } from 'types/visitTypes';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { formatFullName, hasPermission } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { getRequest } from 'api/apiClient';
import VisitorsDrawer from 'components/pages/visitors/drawer/VisitorsDrawer';
import CardInfoModal from 'components/pages/dashboard/CardInfoModal';
import Loader from 'components/customAntd/Loader';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

interface ExtendedVisitor extends Visitor {
    scheduleStart: string | null; // Ensuring each visitor has a scheduleStart
    visitId: number;
}

interface VisitorsTrendChartProps {
    visitors: ExtendedVisitor[];
    startDate: Dayjs;
    endDate: Dayjs;
    visitStatus: VisitStatus[];
}

function VisitorsTrendChart({ visitors, startDate, endDate, visitStatus }: VisitorsTrendChartProps) {
    const { t } = useTranslation();
    const statusKeys: string[] = ['Pending', 'Signed In', 'Signed Out', 'Denied Entry', 'No Show'];


    const generateDateRange = (start: Dayjs, end: Dayjs) => {
        const dates = [];
        let currentDate = start;
        while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
            dates.push(currentDate.format('M/D'));
            currentDate = currentDate.add(1, 'day');
        }
        return dates;
    };

    const labels = generateDateRange(startDate, endDate);

    type StatusData = {
        [key: string]: {
            label: string;
            backgroundColor: string;
            borderColor: string;
            borderWidth: number;
            data: number[];
        };
    };

    const initialStatusData: StatusData = visitStatus.reduce((acc, status) => {
        const name = status.name!;
        acc[name] = {
            label: t(status.languageCode ?? '', name),
            backgroundColor: getStatusColor(name),
            borderColor: getStatusColor(name),
            borderWidth: 1,
            data: Array(labels.length).fill(0),
        };
        return acc;
    }, {
        'No Show': {
            label: t('vst_data_noshow', 'No Show'),
            backgroundColor: getStatusColor('No Show'),
            borderColor: getStatusColor('No Show'),
            borderWidth: 1,
            data: Array(labels.length).fill(0),
        }
    } as StatusData);

    const statusData = visitors.reduce<StatusData>((acc, visitor) => {
        const statusName = getStatusNameById(visitor.status!, visitor.scheduleStart);

        // skip unknown status
        if (!acc[statusName]) return acc;

        let dateKey;
        switch (statusName) {
            case 'Pending':
            case 'No Show':
                dateKey = visitor.scheduleStart;
                break;
            case 'Denied Entry':
            case 'Signed In':
                dateKey = visitor.signIn;
                break;
            case 'Signed Out':
                dateKey = visitor.signOut;
                break;
        }

        if (dateKey && dayjs(dateKey).isBetween(startDate, endDate, 'day', '[]')) {
            const dateLabel = dayjs(dateKey).format('M/D');
            const index = labels.indexOf(dateLabel);
            if (index !== -1) {
                acc[statusName].data[index]++;
            }
        }

        return acc;
    }, initialStatusData);

    const datasets = statusKeys
        .filter(key => statusData[key])
        .map(key => statusData[key]);

    const data = {
        labels,
        datasets
    };

    const [labelColor, setLabelColor] = useState(getComputedStyle(document.documentElement).getPropertyValue('--colorTextBase'));
    const [gridColor, setGridColor] = useState(getComputedStyle(document.documentElement).getPropertyValue('--colorBorderSecondary'));

    useEffect(() => {
        const handleThemeChange = () => {
            setLabelColor(getComputedStyle(document.documentElement).getPropertyValue('--colorTextBase'));
            setGridColor(getComputedStyle(document.documentElement).getPropertyValue('--colorBorderSecondary'));
        };

        // Listen for changes to the theme
        window.addEventListener('themechange', handleThemeChange);

        // Initial set
        handleThemeChange();

        return () => {
            window.removeEventListener('themechange', handleThemeChange);
        };
    }, []);

    const options = {
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    color: labelColor,
                },
                grid: {
                    color: gridColor,
                },
            },
            x: {
                ticks: {
                    color: labelColor,
                },
                grid: {
                    color: gridColor,
                },
            },
        },
        plugins: {
            legend: {
                display: true,
            },
        },
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '280px' }}>
            <Bar data={data} options={options} />
        </div>
    );
}

const VisitorReport: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const { visits, fetchVisitsLoading } = useSelector((state: RootState) => state.visits);
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode); // Accessing isDarkMode from Redux

    const selectedLocationIdRef = useRef<number>(globalLocationId!);
    const startDateRef = useRef(dayjs().startOf('week').format(DATE_TIME_FORMAT));
    const endDateRef = useRef(dayjs().endOf('week').format(DATE_TIME_FORMAT));

    const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
    const [filteredVisitors, setFilteredVisitors] = useState<ExtendedVisitor[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedVisitors, setSelectedVisitors] = useState<ExtendedVisitor[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState<boolean>(false);
    const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);

    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasAllvisitorsRead = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:r');

    const { t } = useTranslation();


    useEffect(() => {
        const fetchVisitStatuses = async () => {
            try {
                const visitorStatus = await getRequest(`/orgs/${orgId}/visitorStatus`);
                setVisitStatus(visitorStatus.data);
            } catch (e) {
                console.error('Failed to fetch visit statuses', e);
            }
        };

        if (orgId) {
            fetchVisitStatuses();
        }
    }, [orgId]);

    const columns = useMemo<OpTableRawColumnType[]>(() => {
        return [
            {
                label: t('visitorReport.date'),
                dataIndex: ['date'],
                width: 150,
                render: (text: any) => dayjs(text).format('YYYY-MM-DD'),
                disabled: true,
            },
            ...visitStatus.map((status) => ({
                label: t(status.languageCode ?? '', status.name),
                key: `counts.${status.name}`,
                dataIndex: ['counts', status.name],
                render: (text: any) => (
                    <span style={{ color: text === 0 && isDarkMode ? 'grey' : text === 0 ? '#dddddd' : 'inherit' }}>
                        {text || 0}
                    </span>
                ),
                width: 150,
            })),
            {
                label: t('vst_data_noshow'),
                key: 'counts.No Show',
                dataIndex: ['counts', 'No Show'],
                render: (text: any) => (
                    <span style={{ color: text === 0 && isDarkMode ? 'grey' : text === 0 ? '#dddddd' : 'inherit' }}>
                        {text || 0}
                    </span>
                ),
                width: 150
            },
            {
                label: t('visitorReport.total'),
                dataIndex: ['total'],
                key: 'total',
                width: 150,
                disabled: true,
            },
        ]
    }, [visitStatus, isDarkMode, t]);

    useEffect(() => {
        const keys = new Set<string>(
            columns.map((col) => col.key).filter(Boolean) as string[]
        );
        setVisibleColumns(keys);
    }, [columns]);


    const statusKeys: StatusKey[] = ['Pending', 'Signed In', 'Signed Out', 'Denied Entry', 'No Show'];
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(statusKeys));

    const fetchVisitsData = async () => {
        await dispatch(setVisitsStartDate(startDateRef.current));
        await dispatch(setVisitsEndDate(endDateRef.current));
        await dispatch(fetchVisits({ orgId }));
    };

    useEffect(() => {
        fetchVisitsData();
        // eslint-disable-next-line
    }, [dispatch, orgId]);

    useEffect(() => {
        if (visits) {
            const filteredVisitors = visits.reduce<ExtendedVisitor[]>((acc, visit) => {
                const visitorsWithSchedule = visit.visitors.map(visitor => {
                    const statusName = getStatusNameById(visitor.status!, visit.scheduleStart);
                    const dateKey = getVisitorDateTime(visitor, statusName, visit?.scheduleStart, DATE_TIME_FORMAT);

                    return {
                        ...visitor,
                        visitId: visit.id,
                        scheduleStart: visit.scheduleStart,
                        dateKey
                    };
                }).filter(visitor => {
                    return visitor.dateKey && dayjs(visitor.dateKey).isBetween(dayjs(startDateRef.current).startOf('day'), dayjs(endDateRef.current).endOf('day'), null, '[]');
                });

                return [...acc, ...visitorsWithSchedule];
            }, []);
            setFilteredVisitors(filteredVisitors);
        }
    }, [visits, globalUserId, hasAllvisitorsRead]);

    const handleDateRangeLocationFilter = (locationId: number, startDate: string, endDate: string) => {
        selectedLocationIdRef.current = locationId;
        startDateRef.current = startDate;
        endDateRef.current = endDate;
        fetchVisitsData();
    };

    const handleVisibleColumnsChange = (newVisibleLabels: Set<Key>) => {
        const newVisibleKeys = new Set<string>(
            columns
                .filter(col => newVisibleLabels.has(col.label!))
                .map(col => col.key as string)
        );
        setVisibleColumns(newVisibleKeys);
    };

    type StatusKey = 'Denied Entry' | 'Pending' | 'Signed In' | 'Signed Out' | 'No Show';

    type StatusCounts = Record<StatusKey, number>;

    interface TableRowData {
        key: string;
        date: string;
        total: number;
        counts: StatusCounts;
    }

    interface DateIndexMap {
        [key: string]: number;
    }

    const prepareTableData = (): TableRowData[] => {
        const dates = [];
        for (let d = dayjs(startDateRef.current); d.isSameOrBefore(dayjs(endDateRef.current)); d = d.add(1, 'day')) {
            dates.push(d.format('YYYY-MM-DD')); // Format date in YYYY-MM-DD
        }

        const tableData = dates.map(date => ({
            key: date,
            date: date,
            total: 0,
            counts: {
                'Denied Entry': 0,
                'Pending': 0,
                'Signed In': 0,
                'Signed Out': 0,
                'No Show': 0,
            }
        }));

        const dateIndexMap: DateIndexMap = tableData.reduce((acc: DateIndexMap, item, index) => {
            acc[item.date] = index;
            return acc;
        }, {});

        filteredVisitors.forEach(visitor => {
            const statusName = getStatusNameById(visitor.status!, visitor.scheduleStart) as StatusKey;
            let dateKey = '';
            switch (statusName) {
                case 'Pending':
                case 'No Show':
                    dateKey = dayjs(visitor.scheduleStart).format('YYYY-MM-DD');
                    break;
                case 'Denied Entry':
                case 'Signed In':
                    dateKey = dayjs(visitor.signIn).format('YYYY-MM-DD');
                    break;
                case 'Signed Out':
                    dateKey = dayjs(visitor.signOut).format('YYYY-MM-DD');
                    break;
            }
            if (dateKey && dateIndexMap[dateKey] !== undefined) {
                const rowIndex = dateIndexMap[dateKey];
                if (tableData[rowIndex].counts[statusName] !== undefined) {
                    tableData[rowIndex].counts[statusName]++;
                }
            }
        });

        tableData.forEach(row => {
            row.total = (Object.keys(row.counts) as StatusKey[]).reduce((acc, key) => {
                if (visibleColumns.has(`counts.${key}`)) {
                    acc += row.counts[key];
                }
                return acc;
            }, 0);
        });

        return tableData.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()); // Sort in decreasing order by date
    };

    const getFormattedVisitors = (visitors: ExtendedVisitor[]) => {
        return visitors.map((visitor) => {
            const statusName = getStatusNameById(visitor.status!, visitor.scheduleStart);
            const dateTime = getVisitorDateTime(visitor, statusName, visitor.scheduleStart, 'MMMM D, YYYY h:mm A') || 'N/A';
            return {
                name: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
                firstName: visitor.firstName ?? undefined,
                lastName: visitor.lastName ?? undefined,
                dateTime,
                statusName,
                visitId: visitor.visitId!,
                visitorId: visitor.id!,
            };
        });
    };

    const handleClickedVisitor = async (visitId: number, visitorId: number) => {
        const selectedVisit = await getRequest(`/orgs/${orgId}/visitor/${visitId}`);
        const selectedVisitor = selectedVisit.data[0].visitors.find((v: Visitor) => Number(v.id) === Number(visitorId));

        dispatch(setSelectedVisit(selectedVisit.data[0]));
        dispatch(setSelectedVisitor(selectedVisitor));
        setIsVisitorsDrawerOpen(true);
        setIsProfileDrawerOpen(true);
        setIsModalVisible(false);
    };

    const handleDrawerClose = () => {
        setIsVisitorsDrawerOpen(false);
        dispatch(setSelectedVisit(null));
    };

    return (
        <OpPage title={t('visitorReport.title')} tooltip={t('visitorReport.tooltip')} >
            <OpSpace
                direction="vertical"
                size="middle"
                style={{
                    display: 'flex',
                }}
            >
                <DateRangeLocationFilter2
                    onDateRangeLocationFilter={handleDateRangeLocationFilter}
                    initialStartDate={startDateRef.current}
                    initialEndDate={endDateRef.current}
                />
                <OpCard type="inner" title={t('visitorReport.chartTitle')} >
                    {fetchVisitsLoading ? (
                        <Loader />
                    ) : (<VisitorsTrendChart visitors={filteredVisitors} startDate={dayjs(startDateRef.current)} endDate={dayjs(endDateRef.current)} visitStatus={visitStatus} />
                    )}
                </OpCard>
                <OpTable
                    dataSource={prepareTableData()}
                    label={`${dayjs(startDateRef.current).format(DATE_FORMAT)} - ${dayjs(endDateRef.current).format(DATE_FORMAT)}`}
                    columns={columns}
                    loading={fetchVisitsLoading}
                    pagination={false}
                    allowGlobalSearch={false}
                    allowExport={true}
                    allowShowHideColumns={true}
                    onVisibleColumnsChange={handleVisibleColumnsChange}
                    rowActions={{
                        onEditClick: (data: any) => {
                            const date = data.date;
                            setSelectedDate(date);

                            const visitorsForDate = filteredVisitors.filter((visitor) => {
                                const statusName = getStatusNameById(visitor.status!, visitor.scheduleStart);
                                let dateKey = '';

                                switch (statusName) {
                                    case 'Pending':
                                    case 'No Show':
                                        dateKey = dayjs(visitor.scheduleStart).format('YYYY-MM-DD');
                                        break;
                                    case 'Signed In':
                                    case 'Denied Entry':
                                        dateKey = dayjs(visitor.signIn).format('YYYY-MM-DD');
                                        break;
                                    case 'Signed Out':
                                        dateKey = dayjs(visitor.signOut).format('YYYY-MM-DD');
                                        break;

                                }
                                return dateKey === date;
                            });

                            setSelectedVisitors(visitorsForDate);
                            setIsModalVisible(true);
                        },
                    }}
                    rowKey={"key"}
                />

                {isModalVisible && (
                    <CardInfoModal
                        visible={isModalVisible}
                        title={selectedDate ? `${selectedDate}` : ''}
                        content={
                            getFormattedVisitors(
                                selectedVisitors.filter((visitor) => { // Apply the same filter as the table filter
                                    const statusName = getStatusNameById(visitor.status!, visitor.scheduleStart);
                                    return visibleColumns.has(`counts.${statusName}`);
                                })
                            ).sort((a, b) => { // Sort by time
                                return dayjs(b.dateTime).valueOf() - dayjs(a.dateTime).valueOf();
                            })
                        }
                        onCancel={() => setIsModalVisible(false)}
                        onItemClick={(visitId, visitorId) => {
                            handleClickedVisitor(visitId, visitorId);
                        }}
                    />
                )}

                {(isVisitorsDrawerOpen) && (
                    <VisitorsDrawer
                        open={isVisitorsDrawerOpen}
                        isProfileDrawerOpen={isProfileDrawerOpen}
                        onClose={handleDrawerClose}
                        setIsProfileDrawerOpen={setIsProfileDrawerOpen}
                    />
                )}
            </OpSpace>
        </OpPage>
    );
}

export default VisitorReport;
